import { FormikProvider, setNestedObjectValues, useFormik } from 'formik';
import Moment from 'moment';
import { PropsWithChildren, ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import { formatDuration } from 'utility/generalHelper';

import { CloseIcon, CopyIcon } from '@treams/treams-icons';
import { Route, useHistory } from '@treams/treams-routes';
import { Tags } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import WarningIcon from 'packages/kakapo-ui-kit/assets/icons/WarningIcon';
import ActionListItem, {
	ActionArchiveListItem,
} from 'packages/kakapo-ui-kit/components/actions/ActionListItem';
import useBreadcrumb from 'packages/kakapo-ui-kit/components/breadcrumb/useBreadcrumb';
import EmptyStateCard from 'packages/kakapo-ui-kit/components/cards/EmptyStateCard';
import DetailsTableForm from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import {
	PageTitle,
	SectionTitle,
	SubSectionTitle,
} from 'packages/kakapo-ui-kit/components/common/Headings';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import Button from 'packages/kakapo-ui-kit/components/editors/Button';
import DeleteButton from 'packages/kakapo-ui-kit/components/editors/DeleteButton';
import EditButton from 'packages/kakapo-ui-kit/components/editors/EditButton';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import TooltipDark from 'packages/kakapo-ui-kit/components/tooltips/TooltipDark';
import { validator } from 'packages/kakapo-ui-services/factories/ValidatorFactory';

import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';
import AddMomentButton from './components/AddMomentButton';
import MomentTemplateModal from './moment-templates/MomentTemplateModal';
import CircleButton from 'packages/kakapo-ui-kit/components/editors/CircleButton';
import { Prompt } from 'react-router-dom';

const TableCell = styled(Box)`
	color: ${(props) => props.theme.colors.slateBlue};
`;

const TableHeader = styled(TableCell)`
	font-weight: bold;
	font-size: 12px;
`;

const Badge = styled(Box)`
	background: ${(props) => props.theme.colors.blue};
	color: white;
	border-radius: 15px;
	height: 30px;
	display: flex;
	align-items: center;
	padding: 0px 12px;
`;

const InlineValidationError = styled(Flex)`
	position: relative;
	align-items: center;
	color: ${(props) => props.theme.colors.red};
`;

const ClearStartdateButton = styled.button`
	cursor: pointer;
	background: none;

	svg path {
		fill: ${(props) => props.theme.colors.slateBlue};
	}
`;

function InlineValidation(props: PropsWithChildren<{ valid: boolean; message: string }>) {
	const [hover, setHover] = useState(false);

	if (props.valid) {
		return <>{props.children}</>;
	}

	return (
		<InlineValidationError onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
			<TooltipDark direction="north" isHovered={hover}>
				{props.message}
			</TooltipDark>
			<Box flexGrow={0} flexShrink={0} mr={2}>
				<WarningIcon color="red" style={{ display: 'block' }} />
			</Box>
			{props.children}
		</InlineValidationError>
	);
}

function mapMomentNotificationToInput(
	momentNotification: graphql.MomentNotification,
	stripID?: boolean,
): graphql.MomentNotificationInput {
	return {
		id: stripID
			? undefined
			: momentNotification.id?.startsWith('_')
			? undefined
			: momentNotification.id,
		offset: momentNotification.offset,
		type: momentNotification.type,
	};
}

function mapMomentFeedbackToInput(
	momentFeedback: graphql.MomentFeedback,
): graphql.MomentFeedbackInput {
	const input: graphql.MomentFeedbackInput = {};

	if (momentFeedback.feedbackTemplate) {
		input.feedbackTemplateID = momentFeedback.feedbackTemplate.id;
	}

	if (momentFeedback.minimumQuestions) {
		input.minimumQuestions = momentFeedback.minimumQuestions;
	}

	if (momentFeedback.minimumRecipients) {
		input.minimumRecipients = momentFeedback.minimumRecipients;
	}

	if (momentFeedback.maximumRecipients) {
		input.maximumRecipients = momentFeedback.maximumRecipients;
	}

	return input;
}

function mapMomentVariationsToInput(
	momentVariations: graphql.MomentVariation[],
): graphql.MomentVariationInput[] {
	return momentVariations.map((momentVariation) => ({
		evaluation: {
			evaluationFormID: momentVariation.evaluation!.evaluationForm!.id,
		},
		goals: momentVariation.goals
			? {
					minimumGoals: momentVariation.goals.minimumGoals,
					minimumCompetencyGoals: (momentVariation.goals as any).allowCompetencyGoals
						? momentVariation.goals.minimumCompetencyGoals
						: undefined,
			  }
			: undefined,
		jobTitleID: momentVariation.jobTitle.id,
	}));
}

function mapMomentToInput(
	moment: graphql.MomentTemplate,
	stripID?: boolean,
): graphql.MomentTemplateInput {
	return {
		id: stripID ? undefined : moment.id.startsWith('_') ? undefined : moment.id,
		duration: moment.duration,
		startOffset: moment.startOffset - 1,
		title: moment.title,
		type: moment.type,
		description: moment.description,
		instruction: moment.instruction,
		evaluation: moment.evaluation
			? {
					evaluationFormID: moment.evaluation.evaluationForm?.id,
			  }
			: undefined,
		feedback: moment.feedback ? mapMomentFeedbackToInput(moment.feedback) : undefined,
		goals: moment.goals
			? {
					minimumGoals: moment.goals.minimumGoals,
					minimumCompetencyGoals: (moment.goals as any).allowCompetencyGoals
						? moment.goals.minimumCompetencyGoals
						: undefined,
			  }
			: undefined,
		variations: moment.variations ? mapMomentVariationsToInput(moment.variations) : undefined,
		notifications: moment.notifications.map((notification) =>
			mapMomentNotificationToInput(notification, stripID),
		),
		showToUser: moment.showToUser,
	};
}

function CycleTemplateEditor(props: { cycleTemplate?: graphql.CycleTemplate }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [previewDate, setPreviewDate] = useState<Date | undefined>();

	useBreadcrumb(
		t(`common.settings.cycleTemplates.${props.cycleTemplate ? 'edit' : 'create'}.title`),
	);

	const formik = useFormik({
		initialValues: {
			title: props.cycleTemplate?.title ?? [],
			description: props.cycleTemplate?.description ?? [],
			successorID: props.cycleTemplate?.successor?.id ?? undefined,
			repeats: !!props.cycleTemplate?.successor,
			duration: props.cycleTemplate?.duration.toFixed() ?? '12',
			automatic: props.cycleTemplate?.automatic ?? false,
			groupIDs: props.cycleTemplate?.groups?.map(({ id }) => id) ?? [],
			excludedGroupIDs: props.cycleTemplate?.excludedGroups?.map(({ id }) => id) ?? [],
			jobTitleIDs: props.cycleTemplate?.jobTitles?.map(({ id }) => id) ?? [],
			excludedJobTitleIDs: props.cycleTemplate?.excludedJobTitles?.map(({ id }) => id) ?? [],
			interval: props.cycleTemplate?.interval ?? undefined,
			employmentType: props.cycleTemplate?.employmentType ?? undefined,
			moments:
				props.cycleTemplate?.moments.map((moment) => ({
					...moment,
					startOffset: moment.startOffset + 1,
					goals: moment.goals && {
						minimumGoals: moment.goals.minimumGoals,
						allowCompetencyGoals: typeof moment.goals.minimumCompetencyGoals === 'number',
						minimumCompetencyGoals: moment.goals.minimumCompetencyGoals ?? 0,
					},
				})) ?? [],
		},
		onSubmit: () => handleSubmit(),
		validationSchema: validator.object({
			title: validator.localizedString(),
			duration: validator.number().min(1),
			moments: validator.array(
				validator.object({
					startOffset: validator.number().min(1),
					duration: validator.number().min(1),
					notifications: validator.array(
						validator.object({
							type: validator.string(),
							offset: validator.number().min(0).max(366),
						}),
					),
				}),
			),
		}),
		enableReinitialize: true,
	});

	const groups = graphql.useGroupsQuery({
		variables: {
			first: 1000,
			filter: {
				isArchived: false,
				types: [graphql.GroupType.Department],
			},
		},
	});

	const jobTitles = graphql.useJobTitlesQuery({
		variables: {
			first: 1000,
			isArchived: false,
		},
	});

	const templates = graphql.useCycleTemplatesQuery({
		variables: {
			first: 1000,
			isArchived: false,
		},
		skip: !formik.values.repeats,
	});

	useEffect(() => {
		if (formik.values.repeats === !!formik.values.successorID) {
			return;
		}

		if (formik.values.repeats) {
			formik.setFieldValue('successorID', props.cycleTemplate?.id ?? '$repeat');
		} else {
			formik.setFieldValue('successorID', undefined);
		}
	}, [formik.values.repeats]);

	useEffect(() => {
		(async () => {
			await formik.validateForm();
			formik.setTouched(setNestedObjectValues(formik.values, true));
		})();
	}, []);

	const [error, attempt, reset] = useErrorBoundary();

	const [create, { loading: createLoading }] = graphql.useCreateCycleTemplateMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateCycleTemplateMutation();
	const loading = createLoading || updateLoading;

	const handleSubmit = attempt(async () => {
		reset();

		const input = {
			title: formik.values.title.map((title) => ({
				languageID: title.languageID,
				text: title.text,
			})),
			description: formik.values.description.map((description) => ({
				languageID: description.languageID,
				text: description.text,
			})),
			automatic: formik.values.automatic,
			groupIDs: formik.values.groupIDs.length ? formik.values.groupIDs : undefined,
			excludedGroupIDs: formik.values.excludedGroupIDs.length
				? formik.values.excludedGroupIDs
				: undefined,
			jobTitleIDs: formik.values.jobTitleIDs.length ? formik.values.jobTitleIDs : undefined,
			excludedJobTitleIDs: formik.values.excludedJobTitleIDs.length
				? formik.values.excludedJobTitleIDs
				: undefined,
			interval: formik.values.interval,
			employmentType: formik.values.employmentType,
		};

		if (props.cycleTemplate) {
			const response = await update({
				variables: {
					input: {
						id: props.cycleTemplate.id,
						duration: parseInt(formik.values.duration),
						successorID: formik.values.successorID,
						moments: formik.values.moments.map((moment) => mapMomentToInput(moment)),
						...input,
					},
				},
			});
		} else {
			const { data } = await create({
				variables: {
					input: {
						moments: formik.values.moments.map((moment) => mapMomentToInput(moment)),
						duration: parseInt(formik.values.duration),
						successorID: formik.values.successorID,
						...input,
					},
				},
			});

			if (data) {
				history.push(`/settings/cycle-templates/${data.createCycleTemplate.cycleTemplate.id}`);
			}
		}

		addToast(t('common.settings.cycleTemplates.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	const handleDuplicate = attempt(async () => {
		const input = {
			title: formik.values.title.map((title) => ({
				languageID: title.languageID,
				text: t('common.copyOf', { text: title.text, lng: title.languageID.split('_')[0] }),
			})),
			description: formik.values.description.map((description) => ({
				languageID: description.languageID,
				text: description.text,
			})),
			automatic: formik.values.automatic,
			groupIDs: formik.values.groupIDs.length ? formik.values.groupIDs : undefined,
			excludedGroupIDs: formik.values.excludedGroupIDs ? formik.values.excludedGroupIDs : undefined,
			jobTitleIDs: formik.values.jobTitleIDs.length ? formik.values.jobTitleIDs : undefined,
			excludedJobTitleIDs: formik.values.excludedJobTitleIDs
				? formik.values.excludedJobTitleIDs
				: undefined,
			interval: formik.values.interval,
			employmentType: formik.values.employmentType,
		};

		const { data } = await create({
			variables: {
				input: {
					moments: formik.values.moments.map((moment) => mapMomentToInput(moment, true)),
					duration: parseInt(formik.values.duration),
					successorID: formik.values.successorID,
					...input,
				},
			},
		});

		if (data) {
			history.push(`/settings/cycle-templates/${data.createCycleTemplate.cycleTemplate.id}`);
		}
	});

	const history = useHistory();

	const { localize, language } = useLocalize();

	const { pathname } = history.location;

	/**
	 * Adds a new moment
	 * @param type - What type of moment it will be
	 */
	const addMomentHandler = (type?: graphql.MomentType, moment?: graphql.MomentTemplate) => {
		const newId = '_' + Math.random().toString();
		const notifications: graphql.MomentNotification[] = [];
		let values: graphql.MomentTemplate = {} as graphql.MomentTemplate;

		// If moment data is given, clone from that
		if (moment) {
			// Filter out id from the data set
			const { id, title, ...rest } = moment;

			let duplicateMomentTitle = concatLocalizedStrings(
				title,
				t('common.settings.cycleTemplates.copy.duplicateMoment'),
			);

			values = {
				id: newId,
				title: duplicateMomentTitle,
				...rest,
			};
		} else if (type) {
			// Add a notification by default (for both normal and message moments)
			// For normal moments the user can remove this notification.
			notifications.push({
				type: graphql.MomentNotificationType.BeforeStart,
				offset: 0,
			} as graphql.MomentNotification);

			values = {
				id: newId,
				duration: 1,
				startOffset: 1,
				notifications,
				isArchived: false,
				title: [
					{
						languageID: language.id,
						text: t('common.settings.cycleTemplates.edit.momentTitlePlaceholder'),
					},
				],
				showToUser: false,
				type,
			};

			history.push(
				`/settings/cycle-templates/${props.cycleTemplate?.id ?? 'create'}/moments/${newId}`,
			);
		}

		formik.setFieldValue('moments', formik.values.moments.concat([values as any]));
	};

	/**
	 * Creates a new moment from an existing moment
	 */
	const cloneMomentHandler = (moment: graphql.MomentTemplate) => {
		addMomentHandler(undefined, moment);
	};

	return (
		<FormikProvider value={formik}>
			<Prompt
				when={formik.dirty}
				message={(location) => {
					if (location.pathname === formatString(pathname)) {
						return true;
					}
					return t('common.settings.cycleTemplates.modal.warning');
				}}
			/>
			<Flex flexDirection="column" mt={3}>
				<Flex flexDirection="row" alignItems="center">
					<PageTitle>
						{t(`common.settings.cycleTemplates.${props.cycleTemplate ? 'edit' : 'create'}.title`)}
					</PageTitle>
				</Flex>
				<Box mt={4} mb={2}>
					<Box mb={3}>
						<SubSectionTitle>{t('common.settings.cycleTemplates.edit.details')}</SubSectionTitle>
					</Box>
					<HorizontalRule />
				</Box>
				<DetailsTableForm
					formValues={[
						{
							label: t('common.settings.cycleTemplates.edit.name'),
							name: 'title',
							type: DetailsTableFormValueTypes.Multilingual,
						},
						{
							label: t('common.settings.cycleTemplates.edit.description'),
							name: 'description',
							type: DetailsTableFormValueTypes.MultilingualQuill,
						},
						{
							label: t('common.settings.cycleTemplates.edit.duration'),
							name: 'duration',
							type: DetailsTableFormValueTypes.Text,
						},
						{
							label: t('common.settings.cycleTemplates.edit.repeats'),
							name: 'repeats',
							type: DetailsTableFormValueTypes.Switch,
						},
						...(formik.values.successorID
							? [
									{
										label: t('common.settings.cycleTemplates.edit.successor.label'),
										name: 'successorID',
										type: DetailsTableFormValueTypes.Select,
										options: [
											{
												label: t('common.settings.cycleTemplates.edit.successor.same'),
												value: props.cycleTemplate?.id ?? '$repeat',
											},
										].concat(
											templates.data?.cycleTemplates.edges
												.filter(({ node }) => node.id !== props.cycleTemplate?.id)
												.map(({ node }) => ({
													label: localize(node.title).text,
													value: node.id,
												})) ?? [],
										),
									},
							  ]
							: []),
					]}
				/>

				<Box mt={4} mb={2}>
					<Box mb={3}>
						<SubSectionTitle>
							{t('common.settings.cycleTemplates.edit.automatic.label')}
						</SubSectionTitle>
						<Box mt={2}>
							<Paragraph>
								{t('common.settings.cycleTemplates.edit.automatic.description')}
							</Paragraph>
						</Box>
					</Box>
					<HorizontalRule />
				</Box>
				<DetailsTableForm
					formValues={[
						{
							label: t('common.settings.cycleTemplates.edit.automatic.enabled.label'),
							name: 'automatic',
							type: DetailsTableFormValueTypes.Switch,
						},
						...(formik.values.automatic
							? [
									{
										label: t('common.settings.cycleTemplates.edit.groups.label'),
										name: 'groupIDs',
										type: DetailsTableFormValueTypes.MultiSelect,
										options: groups.data?.groups.edges.map(({ node }) => ({
											value: node.id,
											label: node.name,
										})),
									},
									{
										label: t('common.settings.cycleTemplates.edit.excludedGroups.label'),
										name: 'excludedGroupIDs',
										type: DetailsTableFormValueTypes.MultiSelect,
										options: groups.data?.groups.edges.map(({ node }) => ({
											value: node.id,
											label: node.name,
										})),
									},
									{
										label: t('common.settings.cycleTemplates.edit.jobTitles.label'),
										name: 'jobTitleIDs',
										type: DetailsTableFormValueTypes.MultiSelect,
										options: jobTitles.data?.jobTitles.edges.map(({ node }) => ({
											value: node.id,
											label: localize(node.title).text,
										})),
									},
									{
										label: t('common.settings.cycleTemplates.edit.excludedJobTitles.label'),
										name: 'excludedJobTitleIDs',
										type: DetailsTableFormValueTypes.MultiSelect,
										options: jobTitles.data?.jobTitles.edges.map(({ node }) => ({
											value: node.id,
											label: localize(node.title).text,
										})),
									},
									{
										label: t('common.settings.cycleTemplates.edit.employmentType.label'),
										name: 'employmentType',
										type: DetailsTableFormValueTypes.Select,
										options: [
											{
												value: undefined as unknown as string,
												label: t('common.settings.cycleTemplates.edit.employmentType.everyone'),
											},
											{
												value: graphql.EmploymentType.Internal,
												label: t('common.settings.cycleTemplates.edit.employmentType.onlyInternal'),
											},
											{
												value: graphql.EmploymentType.External,
												label: t('common.settings.cycleTemplates.edit.employmentType.onlyExternal'),
											},
										],
									},
							  ]
							: []),
						{
							label: t('common.settings.cycleTemplates.edit.interval.label'),
							name: 'interval',
							type: DetailsTableFormValueTypes.Select,
							options: [
								{
									label: t('common.settings.cycleTemplates.edit.interval.options.immediately'),
									value: undefined as unknown as string,
								},
								{
									label: t('common.settings.cycleTemplates.edit.interval.options.yearly'),
									value: graphql.CycleTemplateInterval.Year,
								},
							],
						},
					]}
				/>

				<Box mt={4}>
					<Flex mb={3} alignItems="center">
						<Flex flex={1} alignItems="center">
							<Box mr={2}>
								<SubSectionTitle>
									{t('common.settings.cycleTemplates.edit.moments')}
								</SubSectionTitle>
							</Box>
							<AddMomentButton onSelect={(type) => addMomentHandler(type)} />
						</Flex>
						<TableHeader width={100}>
							{previewDate
								? t('common.settings.cycleTemplates.previewStartDate.start')
								: t('common.settings.cycleTemplates.moments.when')}
						</TableHeader>
						<TableHeader width={100}>
							{previewDate
								? t('common.settings.cycleTemplates.previewStartDate.end')
								: t('common.settings.cycleTemplates.moments.duration')}
						</TableHeader>
						<TableHeader width={100}></TableHeader>
					</Flex>
					<HorizontalRule />
				</Box>

				{formik.values.moments.length === 0 && (
					<EmptyStateCard title={t('common.settings.cycleTemplates.moments.empty')} />
				)}

				{formik.values.moments.map((moment, index) => (
					<Box key={index}>
						<Flex alignItems="center" paddingY={10}>
							<Flex flex={1} alignItems="center">
								<TableCell marginRight={12}>{localize(moment.title).text}</TableCell>
								<Badge>
									{t(`common.settings.cycleTemplates.types.${moment.type.toLowerCase()}`)}
								</Badge>
							</Flex>
							<TableCell width={100}>
								<InlineValidation
									valid={
										moment.startOffset <
											Math.round((365 / 12) * parseInt(formik.values.duration)) &&
										moment.startOffset > 0
									}
									message={t('common.settings.cycleTemplates.whenOutOfRangeError')}
								>
									{previewDate
										? Moment(previewDate)
												.add(moment.startOffset - 1, 'days')
												.format('DD-MM-YYYY')
										: t('common.settings.cycleTemplates.whenFormat', {
												days: moment.startOffset || '0',
										  })}
								</InlineValidation>
							</TableCell>
							<TableCell width={100}>
								<InlineValidation
									valid={moment.duration > 0 && moment.duration <= 100}
									message={t('common.settings.cycleTemplates.durationOutOfRangeError')}
								>
									{previewDate
										? Moment(previewDate)
												.add(moment.startOffset - 1 + moment.duration, 'days')
												.format('DD-MM-YYYY')
										: formatDuration(
												t,
												'common.settings.cycleTemplates.duration',
												moment.duration * 24,
										  ).timeText}
								</InlineValidation>
							</TableCell>
							<TableCell width={100}>
								<Flex>
									{/* Copy moment action */}
									<CircleButton
										color="primary"
										shouldOverrideIconColor
										size={28}
										isLight
										onClick={() => cloneMomentHandler(moment)}
									>
										<CopyIcon />
									</CircleButton>

									<Box ml="12px">
										<EditButton
											size={28}
											onClick={() =>
												history.push(
													`/settings/cycle-templates/${
														props.cycleTemplate?.id ?? 'create'
													}/moments/${moment.id}`,
												)
											}
										/>
									</Box>
									<Box ml="12px">
										<DeleteButton
											onClick={() => {
												const moments = formik.values.moments.slice();
												moments.splice(index, 1);
												formik.setFieldValue('moments', moments);
											}}
										/>
									</Box>
								</Flex>
							</TableCell>
						</Flex>
						<HorizontalRule />
					</Box>
				))}

				<Box mt={4}>
					<GraphQLErrorMessage error={error} />

					<Flex justifyContent="space-between">
						<Button size={32} isLoading={loading} onClick={() => formik.submitForm()}>
							{t('common.save')}
						</Button>
						<Flex flexDirection="row" alignItems="center">
							{previewDate && (
								<ClearStartdateButton type="button" onClick={() => setPreviewDate(undefined)}>
									<Box width={24} height={24}>
										<CloseIcon />
									</Box>
								</ClearStartdateButton>
							)}
							<Tags ml={3}>
								<Tags.Item.DatePicker
									label={t('common.settings.cycleTemplates.previewStartDate.picker')}
									value={previewDate}
									onChange={(date) => setPreviewDate(date)}
								/>
							</Tags>
						</Flex>
					</Flex>
				</Box>

				{props.cycleTemplate && (
					<Box mt={4}>
						<Box my={2}>
							<SectionTitle>{t('common.settings.cycleTemplates.actions.title')}</SectionTitle>
						</Box>

						<Box mb={3}>
							<HorizontalRule />
						</Box>

						<ActionArchiveListItem
							archivableItem={props.cycleTemplate}
							archivableItemName={t('common.settings.cycleTemplates.archivableItemName')}
							archivableItemKey="cycleTemplate"
							useArchive={graphql.useArchiveCycleTemplateMutation as any}
							useRestore={graphql.useRestoreCycleTemplateMutation as any}
						/>

						<ActionListItem
							label={t('common.settings.cycleTemplates.duplicate.label')}
							buttonTitle={t('common.settings.cycleTemplates.duplicate.buttonTitle')}
							onButtonClick={handleDuplicate}
							buttonDisabled={loading || createLoading || formik.dirty}
							disabledText={t('common.settings.cycleTemplates.duplicate.disabled')}
						/>
					</Box>
				)}

				<Route<{ id: string }>
					path={`/settings/cycle-templates/${props.cycleTemplate?.id ?? 'create'}/moments/:id`}
					children={({ match }) => {
						if (!match) {
							return;
						}

						const index = formik.values.moments.findIndex(
							(moment) => moment.id === match.params.id,
						);

						if (index < 0) {
							return;
						}

						return (
							<MomentTemplateModal
								name={`moments[${index}]`}
								url={`/settings/cycle-templates/${props.cycleTemplate?.id ?? 'create'}/moments/${
									match.params.id
								}`}
								returnURL={`/settings/cycle-templates/${props.cycleTemplate?.id ?? 'create'}`}
							/>
						);
					}}
				/>
			</Flex>
		</FormikProvider>
	);
}

function CycleTemplateEditorID(props: { id: string }): ReactElement {
	const { data, error, loading } = graphql.useCycleTemplateQuery({
		variables: { id: props.id },
	});

	if (loading) {
		return <Spinner isLoading />;
	} else if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	} else {
		return <CycleTemplateEditor cycleTemplate={data!.cycleTemplate as graphql.CycleTemplate} />;
	}
}

/**
 * Formats a string by removing the substring '/moments' and everything that follows it.
 *
 * @param input - The input string to be formatted.
 * @returns - The formatted string with '/moments' and everything after it removed,
 *                    or the original string if '/moments' is not found.
 *
 * @example
 * // Returns: "https://example.com"
 * const result = formatString("https://example.com/moments/123");
 */
export function formatString(input: string) {
	const momentsIndex = input.indexOf('/moments');

	if (momentsIndex !== -1) {
		return input.substring(0, momentsIndex);
	} else {
		return input;
	}
}

export function concatLocalizedStrings(lhs: graphql.LocalizedString, rhs: string) {
	return lhs.map(({ languageID, text }) => {
		return {
			languageID,
			text: text + rhs,
		};
	});
}

export { CycleTemplateEditor as CycleTemplateCreator };

export default CycleTemplateEditorID;
